import React, { Dispatch, SetStateAction, useState, useEffect, useRef } from "react";
import { Box, Button, Autocomplete, TextField, Typography } from "@mui/material";
import FormHeader from "../../../components/dms_common/FormHeader";
import FormTextField from "../../../components/dms_common/FormTextField";
import "../../../components/dms_common/FormTextField.css";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BatchComponent from "./BatchComponent";
import { currentStockTypeOptions, stockTypeMapping, normalizeQuantityDisplay } from "./ManualStockAdjustmentUtils";
import Search from "../../../components/dms_common/Search";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import CustomGenericModal from "../../../components/dms_common/CustomGenericModal"; // Updated import
import { BatchAndExpiryManagementRowsData, popupType } from "../../../types/types";
import { transformToBatchwiseRowsData } from "../batchAndExpiryManagement/BatchAndExpiryManagementUtils";
import { ChannelkartNetworkPost, ChannelkartNetworkPut } from "../../../services/networkService";
import { getDataFromLocalDb } from "../../../services/dbService";
import "./AdjustStockLevelsForm.css";
import ScreenOverlay from "../../../components/dms_common/ScreenOverlay";
import { logAnalyticsEvent } from "../../../firebase";
import { EnvironmentRegistry } from "../../../services/registry/environmentRegistry";
import useShortcutKeys from "../../../components/dms_common/ShortcutKeys";

const customMuiInputStyle = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#EAEAEA",
    },
    "&:hover fieldset": {
      borderColor: "#B9B9B9",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#1C7EA0",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#B5B5B5",
    fontSize: "13px",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#1C7EA0",
  },
};

const nonEditableGreyTheme = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FAFAFA",
    "& fieldset": {
      border: "none",
    },
  },
  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
    {
      "-webkit-appearance": "none",
      margin: 0,
    },
};

interface AdjustStockLevelsFormProps {
  setOpenAdjustStockLevelsForm: Dispatch<SetStateAction<boolean>>;
  skuProductDescriptionMap: Record<string, string>;
  skuUomMap: Record<string, string>;
  isCompanyUser?: boolean;
  selectedDistributor?: string | null;
}

const initialFormData = {
  skuName: "",
  skuCode: "",
  batchId: "",
  quantityCs: "0",
  quantityPc: "0",
  quantityOther: "0",
  newQuantityCs: "",
  newQuantityPc: "",
  newQuantityOther: "",
  stockType: "Available",
  batchWiseData: {},
};

interface batchWiseDataType {
  [batchId: string]: {
    quantityCs: string;
    quantityPc: string;
    quantityOther: string;
    newQuantityCs: string;
    newQuantityPc: string;
    newQuantityOther: string;
    reason: string;
    updatedType: string;
  };
}

export interface StockAdjustFormData {
  skuName: string;
  skuCode: string;
  batchId: string;
  quantityCs: string;
  quantityPc: string;
  quantityOther: string;
  newQuantityCs: string;
  newQuantityPc: string;
  newQuantityOther: string;
  stockType: string;
  batchWiseData: batchWiseDataType;
}

const AdjustStockLevelsForm = (props: AdjustStockLevelsFormProps) => {
  const { setOpenAdjustStockLevelsForm, skuProductDescriptionMap, skuUomMap } = props;
  const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false); // Updated state name
  const [openGenericModal, setOpenGenericModal] = useState<boolean>(false);
  const [popupType, setPopupType] = useState<"positive" | "success" | "negative" | "error">("success");
  const [genericMessage, setGenericMessage] = useState<string>("");
  const [confirmationMessage, setConfirmationMessage] = useState<string>("");
  const [formData, setFormData] = useState<StockAdjustFormData>(initialFormData);
  const [isApplyDisabled, setIsApplyDisabled] = useState(true);
  const [allBatchIds, setAllBatchIds] = useState<batchWiseDataType>({});
  const [batchIds, setBatchIds] = useState<string[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [incorrectQuantityError, setIncorrectQuantityError] = useState("");
  const [expandedBatchId, setExpandedBatchId] = useState<string | null>(null);
  const [mandatoryFieldError, setMandatoryFieldError] = useState<boolean>(false);
  const existingBatchIdsRef = useRef<BatchAndExpiryManagementRowsData[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const productsDataRef = useRef<any>(null);

  const skuNameToCodeMap = Object.fromEntries(
    Object.entries(skuProductDescriptionMap).map(([skuCode, skuName]) => [skuName, skuCode])
  );
  const displayedBatchIds = showAll ? batchIds : batchIds.slice(0, 4);
  const { positiveCta, negativeCta } = useShortcutKeys();

  const handleExpand = (batchId: string) => {
    setExpandedBatchId((prevBatchId) => (prevBatchId === batchId ? null : batchId));
  };

  const handleViewMore = () => {
    setShowAll(!showAll);
  };

  const convertFormDataToApiBody = () => {
    const skuDetails = Object.keys(formData.batchWiseData).map((batchId) => {
      const batchData = formData.batchWiseData[batchId];
      const originalBatchData = allBatchIds[batchId];
      
      return {
        batchId: batchId,
        currentPieceBatchQty: Number(originalBatchData.quantityPc),
        updatedPieceBatchQty: Number(batchData.quantityPc),
        currentCaseBatchQty: Number(originalBatchData.quantityCs),
        updatedCaseBatchQty: Number(batchData.quantityCs),
        currentOtherBatchQty: Number(originalBatchData.quantityOther),
        updatedOtherBatchQty: Number(batchData.quantityOther),
        reasonForAdjustment: batchData.reason || "Inventory Reconciliation",
        individualStatus: "Pending",
      };
    });

    return {
      skuCode: formData.skuCode,
      supplier: props.isCompanyUser? props.selectedDistributor : JSON.parse(localStorage.authContext).user.loginId,
      warehouseId: "",
      outletCode: "",
      stockType: formData.stockType,
      currentPieceStockQty: formData.quantityPc,
      updatedPieceStockQty: formData.newQuantityPc ? formData.newQuantityPc : formData.quantityPc,
      currentCaseStockQty: formData.quantityCs,
      updatedCaseStockQty: formData.newQuantityCs ? formData.newQuantityCs : formData.quantityCs,
      currentOtherStockQty: formData.quantityOther,
      updatedOtherStockQty: formData.newQuantityOther
        ? formData.newQuantityOther
        : formData.quantityOther,
      adjustmentDate: new Date().toISOString(),
      skuDetails: skuDetails,
    };
  };

  const handleSaveForm = async () => {
    setConfirmModalOpen(false); // Add this line to close the first modal
    logAnalyticsEvent("manual_stock_adjustment_initiated", {
      distributor_id: JSON.parse(localStorage.authContext).user.loginId,
      sku_code: formData.skuCode,
    });
    try {
      setLoading(true);
      const apiBody = convertFormDataToApiBody();
      const api = props.isCompanyUser? "/consolidatedStock/transformStockAdjustment" : "/consolidatedStock/manualStockAdjustment"
      const res = await ChannelkartNetworkPut(
        `${EnvironmentRegistry.getStockBaseUrl()}${api}`,
        apiBody
      );
      if (res.status !== 200 && res.status !== 201 && res.status !== 204) {
        throw new Error("Bad Request");
      }
      setPopupType("success");
      setGenericMessage("Stock Adjustment submitted Successfully!");
      setOpenGenericModal(true);
      logAnalyticsEvent("manual_stock_adjustment_success", {
        distributor_id: JSON.parse(localStorage.authContext).user.loginId,
        sku_code: apiBody.skuCode,
        stock_type: apiBody.stockType,
        adjusted_qty: apiBody.updatedCaseStockQty,
      });
    } catch (error) {
      setPopupType("error");
      setGenericMessage("Something went Wrong!");
      setOpenGenericModal(true);
      logAnalyticsEvent("manual_stock_adjustment_failed", {
        distributor_id: JSON.parse(localStorage.authContext).user.loginId,
        sku_code: formData.skuCode,
        error_reason: error,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    if (validateBatchWiseData()) {
      setConfirmModalOpen(true); // Updated to use CustomGenericModal
    } else {
      setPopupType("error");
      setGenericMessage("Please select the Batch ID for which stock levels needs adjustment");
      setOpenGenericModal(true);
    }
  };

  const handleSkuCodeChange = (event: React.ChangeEvent<{}>, value: string | null) => {
    setFormData({
      ...initialFormData,
      stockType: formData.stockType,
      skuCode: value || "",
      skuName: value ? skuProductDescriptionMap[value] : "",
    });
  };

  const handleSkuNameChange = (event: React.ChangeEvent<{}>, value: string | null) => {
    setFormData({
      ...initialFormData,
      stockType: formData.stockType,
      skuName: value || "",
      skuCode: value ? skuNameToCodeMap[value] : "",
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === "quantityCs" || name === "quantityPc" || name === "quantityOther") {
      const numericValue = value.replace(/[^0-9]/g, "");
      setFormData({
        ...formData,
        [name]: numericValue,
      });
      return;
    }
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const filterAndTransformData = (stockType: string, skuCode: string) => {
    const quantityFields = stockTypeMapping[stockType] || ["", "", ""];
    return existingBatchIdsRef.current.reduce(
      (acc, item) => {
        const matchesSkuCode = item.skuCode.toLowerCase() === skuCode.toLowerCase();
        const hasRelevantQuantity =
          item[quantityFields[0]] > 0 || item[quantityFields[1]] > 0 || item[quantityFields[2]] > 0;

        if (matchesSkuCode && hasRelevantQuantity) {
          acc.transformedData[item.batchId] = {
            quantityCs: item[quantityFields[1]]?.toString() ?? "0",
            quantityPc: item[quantityFields[0]]?.toString() ?? "0",
            quantityOther: item[quantityFields[2]]?.toString() ?? "0",
            newQuantityCs: item[quantityFields[1]]?.toString() ?? "0",
            newQuantityPc: item[quantityFields[0]]?.toString() ?? "0",
            newQuantityOther: item[quantityFields[2]]?.toString() ?? "0",
            reason: "",
            updatedType: stockType,
          };
          acc.totalQuantityCs += item[quantityFields[1]];
          acc.totalQuantityPc += item[quantityFields[0]];
          acc.totalQuantityOther += item[quantityFields[2]];
        }
        return acc;
      },
      {
        transformedData: {} as batchWiseDataType,
        totalQuantityCs: 0,
        totalQuantityPc: 0,
        totalQuantityOther: 0,
      }
    );
  };

  const setCurrectStockTypeIds = async () => {
    const stockType = formData.stockType.toLowerCase();
    const { transformedData, totalQuantityCs, totalQuantityPc, totalQuantityOther } =
      filterAndTransformData(stockType, formData.skuCode);

    const normalizedTotals = await normalizeQuantityDisplay(
      totalQuantityCs,
      totalQuantityPc,
      totalQuantityOther,
      formData.skuCode
    );

    const normalizedBatchData: any = {};
    for (const batchId of Object.keys(transformedData)) {
      const batchData = transformedData[batchId];
      const normalizedCurrent = await normalizeQuantityDisplay(
        parseInt(batchData.quantityCs),
        parseInt(batchData.quantityPc),
        parseInt(batchData.quantityOther),
        formData.skuCode
      );
      normalizedBatchData[batchId] = {
        ...batchData,
        quantityCs: normalizedCurrent.caseQty.toString(),
        quantityPc: normalizedCurrent.pieceQty.toString(),
        quantityOther: normalizedCurrent.otherQty.toString(),
        newQuantityCs: normalizedCurrent.caseQty.toString(),
        newQuantityPc: normalizedCurrent.pieceQty.toString(),
        newQuantityOther: normalizedCurrent.otherQty.toString(),
      };
    }

    setAllBatchIds(normalizedBatchData);
    setBatchIds(Object.keys(normalizedBatchData));
    setFormData((prevFormData) => ({
      ...prevFormData,
      batchWiseData: {},
      quantityCs: normalizedTotals.caseQty.toString(),
      quantityPc: normalizedTotals.pieceQty.toString(),
      quantityOther: normalizedTotals.otherQty.toString(),
      newQuantityCs: normalizedTotals.caseQty.toString(),
      newQuantityPc: normalizedTotals.pieceQty.toString(),
      newQuantityOther: normalizedTotals.otherQty.toString(),
    }));
  };

  
  useEffect(() => {
    const loadData = async () => {
      await setCurrectStockTypeIds();
    };
    if (formData.stockType && formData.skuCode) {
      loadData();
    }
  }, [formData.stockType, formData.skuCode]);

  const calculateTotalUpdatedQuantities = async () => {
    let totalCaseQty = 0;
    let totalPieceQty = 0;
    let totalOtherQty = 0;

    Object.keys(allBatchIds).forEach((batchId) => {
      const batchData = formData.batchWiseData[batchId] || allBatchIds[batchId];
      totalCaseQty += parseInt(batchData.quantityCs || "0");
      totalPieceQty += parseInt(batchData.quantityPc || "0");
      totalOtherQty += parseInt(batchData.quantityOther || "0");
    });

    const normalizedTotal = await normalizeQuantityDisplay(
      totalCaseQty,
      totalPieceQty,
      totalOtherQty,
      formData.skuCode
    );

    return {
      totalCaseQty: normalizedTotal.caseQty.toString(),
      totalPieceQty: normalizedTotal.pieceQty.toString(),
      totalOtherQty: normalizedTotal.otherQty.toString(),
    };
  };

  useEffect(() => {
    const updateQuantities = async () => {
      if (formData.skuCode && formData.stockType && Object.keys(allBatchIds).length > 0) {
        const { totalCaseQty, totalPieceQty, totalOtherQty } = await calculateTotalUpdatedQuantities();
        setFormData((prevFormData) => ({
          ...prevFormData,
          newQuantityCs: totalCaseQty,
          newQuantityPc: totalPieceQty,
          newQuantityOther: totalOtherQty,
        }));
      }
    };
    updateQuantities();
  }, [formData.batchWiseData, formData.skuCode, formData.stockType, allBatchIds]);

  const handleFormChange = (
    event: React.ChangeEvent<{}>,
    value: string | null,
    field: keyof StockAdjustFormData
  ) => {
    setFormData({
      ...formData,
      [field]: value || "",
    });
  };

  const handleSeachBatchId = (seachValue: string) => {
    const filteredBatchIds = Object.keys(allBatchIds).filter((id: string) =>
      id.toLowerCase().includes(seachValue.toLowerCase())
    );
    setBatchIds(filteredBatchIds);
  };

  useEffect(() => handleSeachBatchId(searchValue), [searchValue]);

  function validateBatchWiseData(): boolean {
    const batchWiseData = formData.batchWiseData;

    if (Object.keys(batchWiseData).length === 0) {
      if (
        (formData.newQuantityOther === "0" ||
          formData.newQuantityCs === "0" ||
          formData.newQuantityPc === "0") &&
        (formData.newQuantityPc === "" || formData.newQuantityPc === "0") &&
        (formData.newQuantityCs === "" || formData.newQuantityCs === "0") &&
        (formData.newQuantityOther === "" || formData.newQuantityOther === "0")
      ) {
        setConfirmationMessage(
          "Are you sure you want to change the entire stock quantity for this SKU to 0?"
        );
        return true;
      } else {
        setGenericMessage("Please select the Batch ID for which stock levels needs adjustment");
        return false;
      }
    }

    setConfirmationMessage("Are you sure you want to submit this Stock Adjustment for approval?");
    return true;
  }

  useEffect(() => {
    const validateForm = () => {
      const isFormValid =
        formData.skuCode &&
        formData.skuName &&
        formData.stockType &&
        !incorrectQuantityError &&
        (formData.newQuantityCs || formData.newQuantityPc || formData.newQuantityOther);
      setIsApplyDisabled(!isFormValid);
    };
    validateForm();
  }, [formData, incorrectQuantityError]);

  const setInitialData = async () => {
    const batchData = await getDataFromLocalDb("BatchWiseStockData");
    existingBatchIdsRef.current = transformToBatchwiseRowsData(batchData.data);
    // Fetch Product data and assign to ref
    const productsData = await getDataFromLocalDb("Product");
    productsDataRef.current = productsData;
  };

  useEffect(() => {
    setInitialData();
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "a") {
        event.preventDefault();
        if (!isApplyDisabled && !loading) {
          handleSubmit();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isApplyDisabled, loading, handleSubmit]);

  return (
    <>
      <div>
        <ScreenOverlay apply={loading} />
        <FormHeader
          parentLabel={"Manual Stock Adjustment"}
          label={"Adjust Stock Levels"}
          goBackToParent={() => setOpenAdjustStockLevelsForm(false)}
        />
        <form className="CreateBatchIdForm">
          <div className="FormTextField">
            <div className="FormTextField_label">
              SKU Code
              <span style={{ color: "red" }}>*</span>
            </div>
            <Autocomplete
              options={Object.keys(skuProductDescriptionMap)}
              getOptionLabel={(option) => option}
              value={formData.skuCode}
              onChange={handleSkuCodeChange}
              sx={{ width: "-webkit-fill-available" }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="skuCode"
                  variant="outlined"
                  size="small"
                  sx={customMuiInputStyle}
                  required
                />
              )}
            />
          </div>
          <div className="FormTextField">
            <div className="FormTextField_label">
              SKU Name
              <span style={{ color: "red" }}>*</span>
            </div>
            <Autocomplete
              options={Object.keys(skuNameToCodeMap)}
              getOptionLabel={(option) => option}
              value={formData.skuName}
              onChange={handleSkuNameChange}
              sx={{ width: "-webkit-fill-available" }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="skuName"
                  variant="outlined"
                  size="small"
                  sx={customMuiInputStyle}
                  required
                />
              )}
            />
          </div>
          <div className="FormTextField">
            <div className="FormTextField_label">
              Current Stock Type
              <span style={{ color: "red" }}>*</span>
            </div>
            <Autocomplete
              disabled
              options={currentStockTypeOptions}
              getOptionLabel={(option) => option}
              value={formData.stockType}
              onChange={(event, value) => handleFormChange(event, value, "stockType")}
              sx={{ width: "-webkit-fill-available" }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="stockType"
                  variant="outlined"
                  size="small"
                  sx={customMuiInputStyle}
                  required
                  placeholder="Select a type"
                />
              )}
            />
          </div>
          {formData.stockType && formData.skuCode && formData.skuName && (
            <>
              <FormTextField
                name={""}
                value={""}
                label={"Current Quantity"}
                handleChange={handleChange}
                isMultiQtyType={true}
                csQty={formData.quantityCs.toString()}
                pcQty={formData.quantityPc.toString()}
                otherQty={formData.quantityOther.toString()}
                isDisabled={true}
                customTheme={nonEditableGreyTheme}
                otherUnitName={productsDataRef.current?.data?.[0]?.data?.[formData.skuCode]?.otherUnitName ?? ""}
              />
              <FormTextField
                name={""}
                nameCs={"newQuantityCs"}
                namePc={"newQuantityPc"}
                nameOther={"newQuantityOther"}
                value={""}
                label={"Updated Quantity"}
                handleChange={handleChange}
                isMandatory={true}
                isMultiQtyType={true}
                csQty={formData.newQuantityCs.toString()}
                pcQty={formData.newQuantityPc.toString()}
                otherQty={formData.newQuantityOther.toString()}
                error={incorrectQuantityError}
                uom={skuUomMap[formData.skuCode]}
                isDisabled={true}
                customTheme={nonEditableGreyTheme}
                otherUnitName={productsDataRef.current?.data?.[0]?.data?.[formData.skuCode]?.otherUnitName ?? ""}
              />
              <div className="FormTextField">
                <div className="edit_batch_label">Edit Batch Wise Qty.</div>
                <div className="batch_ids_container">
                  <Box display="flex" justifyContent="space-between">
                    <Box
                      display="flex"
                      alignItems="center"
                      sx={{ color: "#28A745", fontWeight: 600, fontSize: "14px", gap: "5px" }}
                    >
                      <CheckCircleIcon sx={{ fontSize: "14px !important" }} />
                      {batchIds.length} Batch IDs Available
                    </Box>
                    <div className="searchWidth">
                      <Search label="Search Batch ID" searchFunction={(value) => setSearchValue(value)} />
                    </div>
                  </Box>
                  <div className="batch_ids_block">
                    {displayedBatchIds.length !== 0 ? (
                      displayedBatchIds.map((batchId) => {
                        return (
                          <BatchComponent
                            key={batchId}
                            batchNumber={batchId}
                            currentCs={allBatchIds[batchId]?.quantityCs}
                            currentPc={allBatchIds[batchId]?.quantityPc}
                            currentOther={allBatchIds[batchId]?.quantityOther}
                            formData={formData}
                            setFormData={setFormData}
                            expandedBatchId={expandedBatchId}
                            onExpand={handleExpand}
                            mandatoryFieldError={mandatoryFieldError}
                            setMandatoryFieldError={setMandatoryFieldError}
                            uom={skuUomMap[formData.skuCode]}
                            shouldPrefillQuantities={true}
                            otherUnitName={productsDataRef.current?.data?.[0]?.data?.[formData.skuCode]?.otherUnitName ?? ""}
                          />
                        );
                      })
                    ) : (
                      <div className="no_batch_found">No Batch IDs Found</div>
                    )}
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <span
                        className={`${batchIds.length > 4 ? "view_more" : "view_more hide_view"}`}
                        onClick={handleViewMore}
                      >
                        {showAll ? "view less" : `view ${Object.keys(allBatchIds).length - 4} more`}
                      </span>
                    </Box>
                  </div>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={"3px"}
                    marginTop={"5px"}
                    sx={{
                      backgroundColor: "#FFF9EF",
                      borderRadius: "10px",
                      width: "fit-content",
                      padding: "3px 9px",
                    }}
                  >
                  </Box>
                </div>
              </div>
            </>
          )}
          <Box mt={6} display="flex" justifyContent="right" width={"95%"} margin={"0"}>
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              disabled={isApplyDisabled}
              sx={{
                backgroundColor: isApplyDisabled ? "#E0E0E0" : "primary.main",
                color: isApplyDisabled ? "#A0A0A0" : "#FFFFFF",
              }}
            >
              SUBMIT FOR APPROVAL
              <span style={{ fontSize: "9.5px", textTransform: "none" }}>{positiveCta}</span>
            </Button>
          </Box>
        </form>
      </div>
      <CustomGenericModal
        type="positive"
        message={confirmationMessage}
        modalOpen={confirmModalOpen}
        leftButtonText="Confirm"
        rightButtonText="Cancel"
        onLeftButtonClick={handleSaveForm}
        onRightButtonClick={() => setConfirmModalOpen(false)}
        onClose={() => setConfirmModalOpen(false)}
        loading={loading}
      />
      <CustomGenericModal
        type={popupType}
        message={genericMessage}
        modalOpen={openGenericModal}
        leftButtonText="OK"
        onLeftButtonClick={() => {
          setOpenGenericModal(false);
          if (validateBatchWiseData()) {
            setOpenAdjustStockLevelsForm(false);
          }
        }}
        onClose={() => {
          setOpenGenericModal(false);
          if (validateBatchWiseData()) {
            setOpenAdjustStockLevelsForm(false);
          }
        }}
      />
    </>
  );
};

export default AdjustStockLevelsForm;